import React, { useState, useEffect } from 'react';
import { 
  Save, Edit, Eye, Plus, Trash2, X, Upload, Image, Users, Target, Award, CheckCircle,
  Globe, Zap, Code, Smartphone, Palette, Database, Cloud, Shield, Settings, Mail, Phone, MapPin,
  Play, FileText
} from 'lucide-react';
import { aboutAPI } from '../../services/api';
import { useNotificationHook } from '../../hooks/useNotificationHook';

const AboutUsManagement = () => {
  const { notify } = useNotificationHook();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aboutData, setAboutData] = useState({
    main_title: 'About Niswa Info Tech',
    main_title_simple: 'About',
    gradient_title: 'Niswa Info Tech',
    main_description: 'Transforming businesses through innovative digital solutions since 2019',
    story_title: 'Our Story',
    story_description: '',
    stats_projects_completed: 500,
    stats_happy_clients: 200,
    stats_years_experience: 5,
    stats_team_members: 50,
    stats_items: [],
    what_we_do_title: 'What We Do',
    what_we_do_items: [],
    features_title: 'Our Core Values',
    features_description: 'These principles guide everything we do and shape how we work with our clients',
    features_items: [],
    team_title: 'Meet Our Team',
    team_description: 'The talented individuals behind our success',
    video_title: 'Company Video',
    video_description: 'Watch our company overview and learn more about our mission, values, and the team behind our success.',
    video_url: '',
    video_type: 'youtube',
    video_thumbnail: '',
    documents_title: 'Company Documents and Files',
    documents_description: 'Access important company documents, certificates, and files that showcase our credentials and achievements.',
    cta_title: 'Ready to Transform Your Business?',
    cta_description: 'Let\'s discuss your project and create something amazing together. Get a free consultation and quote today.',
    cta_button1_text: 'Start Your Project',
    cta_button1_link: '/contact',
    cta_button_text: 'Get Free Quote',
    cta_button_link: '/quote'
  });

  const [activeSection, setActiveSection] = useState('main');
  const [isEditing, setIsEditing] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [documentForm, setDocumentForm] = useState({
    title: '',
    description: '',
    file_url: '',
    file_type: 'pdf',
    file_size: '',
    download_button_text: 'Download',
    download_button_icon: 'Download',
    sort_order: 0,
    is_active: true
  });
  const [editingTeamMember, setEditingTeamMember] = useState(null);
  const [teamMemberForm, setTeamMemberForm] = useState({
    name: '',
    role: '',
    bio: '',
    image: '',
    skills: [],
    email: '',
    linkedin_url: '',
    twitter_url: '',
    github_url: '',
    website_url: '',
    sort_order: 0,
    is_active: true
  });

  const sections = [
    { id: 'main', label: 'Main Section', icon: Target },
    { id: 'story', label: 'Story Section', icon: Users },
    { id: 'stats', label: 'Statistics', icon: Award },
    { id: 'what-we-do', label: 'What We Do', icon: Code },
    { id: 'features', label: 'Core Values', icon: CheckCircle },
    { id: 'team', label: 'Team Section', icon: Users },
    { id: 'video', label: 'Video Section', icon: Play },
    { id: 'documents', label: 'Documents Section', icon: FileText },
    { id: 'cta', label: 'Call to Action', icon: Globe }
  ];

  // Fetch about us data
  useEffect(() => {
    fetchAboutData();
    fetchTeamMembers();
    fetchDocuments();
  }, []);

  const fetchAboutData = async () => {
    try {
      setLoading(true);
      const response = await aboutAPI.getContent();
      
      if (response?.data?.success) {
        const data = response.data.data;
        setAboutData({
          main_title: data.main_title || '',
          main_title_simple: data.main_title_simple || '',
          gradient_title: data.gradient_title || '',
          main_description: data.main_description || '',
          story_title: data.story_title || '',
          story_description: data.story_description || '',
          stats_projects_completed: data.stats_projects_completed || 500,
          stats_happy_clients: data.stats_happy_clients || 200,
          stats_years_experience: data.stats_years_experience || 5,
          stats_team_members: data.stats_team_members || 50,
          stats_items: data.stats_items || [],
          what_we_do_title: data.what_we_do_title || '',
          what_we_do_items: data.what_we_do_items || [],
          features_title: data.features_title || '',
          features_description: data.features_description || '',
          features_items: data.features_items || [],
          team_title: data.team_title || '',
          team_description: data.team_description || '',
          video_title: data.video_title || 'Company Video',
          video_description: data.video_description || '',
          video_url: data.video_url || '',
          video_type: data.video_type || 'youtube',
          video_thumbnail: data.video_thumbnail || '',
          documents_title: data.documents_title || 'Company Documents and Files',
          documents_description: data.documents_description || '',
          cta_title: data.cta_title || '',
          cta_description: data.cta_description || '',
          cta_button1_text: data.cta_button1_text || 'Start Your Project',
          cta_button1_link: data.cta_button1_link || '/contact',
          cta_button_text: data.cta_button_text || 'Get Free Quote',
          cta_button_link: data.cta_button_link || '/quote'
        });
      }
    } catch (err) {
      console.error('Error fetching about data:', err);
      setError(`Error loading about data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await aboutAPI.getAllTeamMembers();
      if (response?.data?.success) {
        setTeamMembers(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching team members:', err);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await aboutAPI.getAllDocuments();
      if (response?.data?.success) {
        setDocuments(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setAboutData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayChange = (field, index, key, value) => {
    setAboutData(prev => {
      const newArray = [...prev[field]];
      if (typeof key === 'string') {
        newArray[index] = { ...newArray[index], [key]: value };
      } else {
        newArray[index] = value;
      }
      return { ...prev, [field]: newArray };
    });
  };

  const addArrayItem = (field, defaultValue = {}) => {
    setAboutData(prev => ({
      ...prev,
      [field]: [...prev[field], defaultValue]
    }));
  };

  const removeArrayItem = (field, index) => {
    const itemName = getItemName(field, index);
    if (window.confirm(`Are you sure you want to delete this ${itemName}? This action cannot be undone.`)) {
      const newArray = aboutData[field].filter((_, i) => i !== index);
      setAboutData(prev => ({
        ...prev,
        [field]: newArray
      }));
    }
  };

  // Helper function to get human-readable item names
  const getItemName = (field, index) => {
    const fieldNames = {
      stats_items: 'statistic',
      what_we_do_items: 'service',
      features_items: 'core value'
    };
    return fieldNames[field] || 'item';
  };

  const saveAboutData = async () => {
    try {
      setSaving(true);
      setError(null);
      // Only send the fields that the backend expects
      const payload = {
        main_title: aboutData.main_title,
        main_title_simple: aboutData.main_title_simple,
        gradient_title: aboutData.gradient_title,
        main_description: aboutData.main_description,
        story_title: aboutData.story_title,
        story_description: aboutData.story_description,
        stats_projects_completed: aboutData.stats_projects_completed,
        stats_happy_clients: aboutData.stats_happy_clients,
        stats_years_experience: aboutData.stats_years_experience,
        stats_team_members: aboutData.stats_team_members,
        stats_items: aboutData.stats_items,
        what_we_do_title: aboutData.what_we_do_title,
        what_we_do_items: aboutData.what_we_do_items,
        features_title: aboutData.features_title,
        features_description: aboutData.features_description,
        features_items: aboutData.features_items,
        team_title: aboutData.team_title,
        team_description: aboutData.team_description,
        video_title: aboutData.video_title,
        video_description: aboutData.video_description,
        video_url: aboutData.video_url,
        video_type: aboutData.video_type,
        video_thumbnail: aboutData.video_thumbnail,
        documents_title: aboutData.documents_title,
        documents_description: aboutData.documents_description,
        cta_title: aboutData.cta_title,
        cta_description: aboutData.cta_description,
        cta_button1_text: aboutData.cta_button1_text,
        cta_button1_link: aboutData.cta_button1_link,
        cta_button_text: aboutData.cta_button_text,
        cta_button_link: aboutData.cta_button_link
      };
      const response = await aboutAPI.updateContent(payload);
      if (response?.data?.success) {
        notify.success('About us content updated successfully!', 'Success');
        setIsEditing(false);
      } else {
        setError('Failed to update about us content');
        notify.error('Failed to update about us content', 'Update Error');
      }
    } catch (err) {
      console.error('Error saving about data:', err);
      setError(`Error saving about data: ${err.message}`);
      notify.error(`Error saving about data: ${err.message}`, 'Save Error');
    } finally {
      setSaving(false);
    }
  };

  // Team Management Functions
  const openTeamModal = (member = null) => {
    if (member) {
      setEditingTeamMember(member);
      setTeamMemberForm({
        name: member.name,
        role: member.role,
        bio: member.bio,
        image: member.image,
        skills: member.skills || [],
        email: member.email,
        linkedin_url: member.linkedin_url,
        twitter_url: member.twitter_url,
        github_url: member.github_url,
        website_url: member.website_url,
        sort_order: member.sort_order,
        is_active: member.is_active
      });
    } else {
      setEditingTeamMember(null);
      setTeamMemberForm({
        name: '',
        role: '',
        bio: '',
        image: '',
        skills: [],
        email: '',
        linkedin_url: '',
        twitter_url: '',
        github_url: '',
        website_url: '',
        sort_order: 0,
        is_active: true
      });
    }
    setShowTeamModal(true);
  };

  const closeTeamModal = () => {
    setShowTeamModal(false);
    setEditingTeamMember(null);
    setTeamMemberForm({
      name: '',
      role: '',
      bio: '',
      image: '',
      skills: [],
      email: '',
      linkedin_url: '',
      twitter_url: '',
      github_url: '',
      website_url: '',
      sort_order: 0,
      is_active: true
    });
  };

  const handleTeamMemberChange = (field, value) => {
    setTeamMemberForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addSkill = () => {
    const newSkill = prompt('Enter skill name:');
    if (newSkill && newSkill.trim()) {
      setTeamMemberForm(prev => ({
        ...prev,
        skills: [...prev.skills, newSkill.trim()]
      }));
    }
  };

  const removeSkill = (index) => {
    setTeamMemberForm(prev => ({
      ...prev,
      skills: prev.skills.filter((_, i) => i !== index)
    }));
  };

  const saveTeamMember = async () => {
    try {
      if (editingTeamMember) {
        await aboutAPI.updateTeamMember(editingTeamMember.id, teamMemberForm);
        notify.success('Team member updated successfully!', 'Success');
      } else {
        await aboutAPI.createTeamMember(teamMemberForm);
        notify.success('Team member created successfully!', 'Success');
      }
      closeTeamModal();
      fetchTeamMembers();
    } catch (err) {
      console.error('Error saving team member:', err);
      notify.error(`Error saving team member: ${err.message}`, 'Save Error');
    }
  };

  const deleteTeamMember = async (id) => {
    if (window.confirm('Are you sure you want to delete this team member?')) {
      try {
        await aboutAPI.deleteTeamMember(id);
        notify.success('Team member deleted successfully!', 'Success');
        fetchTeamMembers();
      } catch (err) {
        console.error('Error deleting team member:', err);
        notify.error(`Error deleting team member: ${err.message}`, 'Delete Error');
      }
    }
  };

  // Document Management Functions
  const openDocumentModal = (document = null) => {
    if (document) {
      setEditingDocument(document);
      setDocumentForm({
        title: document.title,
        description: document.description,
        file_url: document.file_url,
        file_type: document.file_type,
        file_size: document.file_size,
        download_button_text: document.download_button_text,
        download_button_icon: document.download_button_icon,
        sort_order: document.sort_order,
        is_active: document.is_active
      });
    } else {
      setEditingDocument(null);
      setDocumentForm({
        title: '',
        description: '',
        file_url: '',
        file_type: 'pdf',
        file_size: '',
        download_button_text: 'Download',
        download_button_icon: 'Download',
        sort_order: 0,
        is_active: true
      });
    }
    setShowDocumentModal(true);
  };

  const closeDocumentModal = () => {
    setShowDocumentModal(false);
    setEditingDocument(null);
    setDocumentForm({
      title: '',
      description: '',
      file_url: '',
      file_type: 'pdf',
      file_size: '',
      download_button_text: 'Download',
      download_button_icon: 'Download',
      sort_order: 0,
      is_active: true
    });
  };

  const handleDocumentChange = (field, value) => {
    setDocumentForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveDocument = async () => {
    try {
      if (editingDocument) {
        await aboutAPI.updateDocument(editingDocument.id, documentForm);
        notify.success('Document updated successfully!', 'Success');
      } else {
        await aboutAPI.createDocument(documentForm);
        notify.success('Document created successfully!', 'Success');
      }
      closeDocumentModal();
      fetchDocuments();
    } catch (err) {
      console.error('Error saving document:', err);
      notify.error(`Error saving document: ${err.message}`, 'Save Error');
    }
  };

  const deleteDocument = async (id) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await aboutAPI.deleteDocument(id);
        notify.success('Document deleted successfully!', 'Success');
        fetchDocuments();
      } catch (err) {
        console.error('Error deleting document:', err);
        notify.error(`Error deleting document: ${err.message}`, 'Delete Error');
      }
    }
  };

  const renderMainSection = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Main Section</h3>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Main Title (Simple)
        </label>
        <input
          type="text"
          value={aboutData.main_title_simple}
          onChange={(e) => handleInputChange('main_title_simple', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="About"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">This will appear in the same color as "Our" on the Services page</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Gradient Title
        </label>
        <input
          type="text"
          value={aboutData.gradient_title}
          onChange={(e) => handleInputChange('gradient_title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Niswa Info Tech"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">This will appear with the same gradient as "Services" on the Services page</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Main Description
        </label>
        <textarea
          value={aboutData.main_description}
          onChange={(e) => handleInputChange('main_description', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Transforming businesses through innovative digital solutions since 2019"
        />
      </div>
    </div>
  );

  const renderStorySection = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Story Section</h3>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Story Title
        </label>
        <input
          type="text"
          value={aboutData.story_title}
          onChange={(e) => handleInputChange('story_title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Our Story"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Story Description
        </label>
        <textarea
          value={aboutData.story_description}
          onChange={(e) => handleInputChange('story_description', e.target.value)}
          rows={6}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Tell your company story..."
        />
      </div>
    </div>
  );

  const renderStatsSection = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Statistics Section</h3>
      
      <div className="flex justify-between items-center mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Statistics Items
        </label>
        <button
          onClick={() => addArrayItem('stats_items', { title: '', value: '', icon: 'Award' })}
          className="flex items-center gap-2 px-3 py-1 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700"
        >
          <Plus className="w-4 h-4" />
          Add Statistic
        </button>
      </div>

      <div className="space-y-4">
        {aboutData.stats_items?.map((item, index) => (
          <div key={index} className="border border-gray-300 dark:border-gray-600 rounded-lg p-4">
            <div className="flex justify-between items-start mb-4">
              <h4 className="font-medium">Statistic {index + 1}</h4>
              <button
                onClick={() => removeArrayItem('stats_items', index)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={item.title}
                  onChange={(e) => handleArrayChange('stats_items', index, 'title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., Projects Completed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Value
                </label>
                <input
                  type="text"
                  value={item.value}
                  onChange={(e) => handleArrayChange('stats_items', index, 'value', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., 500+"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Icon
                </label>
                <select
                  value={item.icon}
                  onChange={(e) => handleArrayChange('stats_items', index, 'icon', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="Award">Award (Badge)</option>
                  <option value="Heart">Heart</option>
                  <option value="Users">Users (Group)</option>
                  <option value="Clock">Clock</option>
                  <option value="Target">Target</option>
                  <option value="CheckCircle">Check Circle</option>
                  <option value="Star">Star</option>
                  <option value="Globe">Globe</option>
                  <option value="Zap">Zap (Lightning)</option>
                  <option value="Code">Code</option>
                  <option value="Smartphone">Smartphone</option>
                  <option value="Palette">Palette</option>
                  <option value="Database">Database</option>
                  <option value="Cloud">Cloud</option>
                  <option value="Shield">Shield</option>
                </select>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderWhatWeDoSection = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">What We Do Section</h3>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Section Title
        </label>
        <input
          type="text"
          value={aboutData.what_we_do_title}
          onChange={(e) => handleInputChange('what_we_do_title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="What We Do"
        />
      </div>

      <div>
        <div className="flex justify-between items-center mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Services
          </label>
          <button
            onClick={() => addArrayItem('what_we_do_items', { title: '', description: '', icon: 'Code' })}
            className="flex items-center gap-2 px-3 py-1 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700"
          >
            <Plus className="w-4 h-4" />
            Add Service
          </button>
        </div>

        <div className="space-y-4">
          {aboutData.what_we_do_items.map((item, index) => (
            <div key={index} className="border border-gray-300 dark:border-gray-600 rounded-lg p-4">
              <div className="flex justify-between items-start mb-4">
                <h4 className="font-medium">Service {index + 1}</h4>
                <button
                  onClick={() => removeArrayItem('what_we_do_items', index)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={item.title}
                    onChange={(e) => handleArrayChange('what_we_do_items', index, { ...item, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Web Development"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => handleArrayChange('what_we_do_items', index, { ...item, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Custom websites and web applications"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Icon
                  </label>
                  <select
                    value={item.icon}
                    onChange={(e) => handleArrayChange('what_we_do_items', index, { ...item, icon: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="Code">Code</option>
                    <option value="Smartphone">Smartphone</option>
                    <option value="TrendingUp">TrendingUp</option>
                    <option value="Globe">Globe</option>
                    <option value="Palette">Palette</option>
                    <option value="Database">Database</option>
                    <option value="Cloud">Cloud</option>
                    <option value="Shield">Shield</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderFeaturesSection = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Core Values Section</h3>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Section Title
        </label>
        <input
          type="text"
          value={aboutData.features_title}
          onChange={(e) => handleInputChange('features_title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Our Core Values"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Section Description
        </label>
        <textarea
          value={aboutData.features_description}
          onChange={(e) => handleInputChange('features_description', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="These principles guide everything we do..."
        />
      </div>

      <div>
        <div className="flex justify-between items-center mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Core Values
          </label>
          <button
            onClick={() => addArrayItem('features_items', { title: '', description: '', icon: 'Target', color: 'from-blue-600 to-blue-700' })}
            className="flex items-center gap-2 px-3 py-1 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700"
          >
            <Plus className="w-4 h-4" />
            Add Value
          </button>
        </div>

        <div className="space-y-4">
          {aboutData.features_items.map((item, index) => (
            <div key={index} className="border border-gray-300 dark:border-gray-600 rounded-lg p-4">
              <div className="flex justify-between items-start mb-4">
                <h4 className="font-medium">Value {index + 1}</h4>
                <button
                  onClick={() => removeArrayItem('features_items', index)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={item.title}
                    onChange={(e) => handleArrayChange('features_items', index, { ...item, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Mission-Driven"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => handleArrayChange('features_items', index, { ...item, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Description..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Icon
                  </label>
                  <select
                    value={item.icon}
                    onChange={(e) => handleArrayChange('features_items', index, { ...item, icon: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="Target">Target</option>
                    <option value="Users">Users</option>
                    <option value="Zap">Zap</option>
                    <option value="Shield">Shield</option>
                    <option value="Award">Award</option>
                    <option value="CheckCircle">CheckCircle</option>
                    <option value="Star">Star</option>
                    <option value="Heart">Heart</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Color
                  </label>
                  <select
                    value={item.color}
                    onChange={(e) => handleArrayChange('features_items', index, { ...item, color: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="from-blue-600 to-blue-700">Blue</option>
                    <option value="from-purple-600 to-purple-700">Purple</option>
                    <option value="from-pink-600 to-pink-700">Pink</option>
                    <option value="from-green-600 to-green-700">Green</option>
                    <option value="from-red-600 to-red-700">Red</option>
                    <option value="from-yellow-600 to-yellow-700">Yellow</option>
                    <option value="from-indigo-600 to-indigo-700">Indigo</option>
                    <option value="from-teal-600 to-teal-700">Teal</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderVideoSection = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Video Section</h3>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Video Title
        </label>
        <input
          type="text"
          value={aboutData.video_title}
          onChange={(e) => handleInputChange('video_title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Company Video"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Video Description
        </label>
        <textarea
          value={aboutData.video_description}
          onChange={(e) => handleInputChange('video_description', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Watch our company overview and learn more about our mission, values, and the team behind our success."
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Video URL
          </label>
          <input
            type="url"
            value={aboutData.video_url}
            onChange={(e) => handleInputChange('video_url', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="https://www.youtube.com/watch?v=..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Video Type
          </label>
          <select
            value={aboutData.video_type}
            onChange={(e) => handleInputChange('video_type', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="youtube">YouTube</option>
            <option value="vimeo">Vimeo</option>
            <option value="drive">Google Drive</option>
            <option value="s3">AWS S3</option>
            <option value="direct">Direct URL</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Video Thumbnail URL (Optional)
        </label>
        <input
          type="url"
          value={aboutData.video_thumbnail}
          onChange={(e) => handleInputChange('video_thumbnail', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="https://example.com/thumbnail.jpg"
        />
      </div>
    </div>
  );

  const renderTeamSection = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Team Section</h3>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Section Title
        </label>
        <input
          type="text"
          value={aboutData.team_title}
          onChange={(e) => handleInputChange('team_title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Meet Our Team"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Section Description
        </label>
        <textarea
          value={aboutData.team_description}
          onChange={(e) => handleInputChange('team_description', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="The talented individuals behind our success"
        />
      </div>

      <div>
        <div className="flex justify-between items-center mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Team Members
          </label>
          <button
            onClick={() => openTeamModal()}
            className="flex items-center gap-2 px-3 py-1 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700"
          >
            <Plus className="w-4 h-4" />
            Add Team Member
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {teamMembers.map((member) => (
            <div key={member.id} className="border border-gray-300 dark:border-gray-600 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <img 
                  src={member.image} 
                  alt={member.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 dark:text-white">{member.name}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{member.role}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => openTeamModal(member)}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteTeamMember(member.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{member.bio}</p>
              <div className="flex flex-wrap gap-1">
                {member.skills?.map((skill, index) => (
                  <span key={index} className="px-2 py-1 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 text-xs rounded">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderCTASection = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Call to Action Section</h3>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          CTA Title
        </label>
        <input
          type="text"
          value={aboutData.cta_title}
          onChange={(e) => handleInputChange('cta_title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Ready to Work Together?"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          CTA Description
        </label>
        <textarea
          value={aboutData.cta_description}
          onChange={(e) => handleInputChange('cta_description', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Let's discuss your project..."
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Button 1 Text
          </label>
          <input
            type="text"
            value={aboutData.cta_button1_text}
            onChange={(e) => handleInputChange('cta_button1_text', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="Start Your Project"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Button 1 Link
          </label>
          <input
            type="text"
            value={aboutData.cta_button1_link}
            onChange={(e) => handleInputChange('cta_button1_link', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="/contact"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Button 2 Text
          </label>
          <input
            type="text"
            value={aboutData.cta_button_text}
            onChange={(e) => handleInputChange('cta_button_text', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="Get Free Quote"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Button 2 Link
          </label>
          <input
            type="text"
            value={aboutData.cta_button_link}
            onChange={(e) => handleInputChange('cta_button_link', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="/quote"
          />
        </div>
      </div>
    </div>
  );

  const renderDocumentsSection = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Documents Section</h3>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Section Title
        </label>
        <input
          type="text"
          value={aboutData.documents_title}
          onChange={(e) => handleInputChange('documents_title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Company Documents and Files"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Section Description
        </label>
        <textarea
          value={aboutData.documents_description}
          onChange={(e) => handleInputChange('documents_description', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Access important company documents, certificates, and files that showcase our credentials and achievements."
        />
      </div>

      <div>
        <div className="flex justify-between items-center mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Company Documents
          </label>
          <button
            onClick={() => openDocumentModal()}
            className="flex items-center gap-2 px-3 py-1 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700"
          >
            <Plus className="w-4 h-4" />
            Add Document
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map((document) => (
            <div key={document.id} className="border border-gray-300 dark:border-gray-600 rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 dark:text-white">{document.title}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{document.description}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {document.file_type} • {document.file_size}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => openDocumentModal(document)}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteDocument(document.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 text-xs rounded">
                  {document.download_button_text}
                </span>
                {!document.is_active && (
                  <span className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs rounded">
                    Inactive
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'main':
        return renderMainSection();
      case 'story':
        return renderStorySection();
      case 'stats':
        return renderStatsSection();
      case 'what-we-do':
        return renderWhatWeDoSection();
      case 'features':
        return renderFeaturesSection();
      case 'team':
        return renderTeamSection();
      case 'video':
        return renderVideoSection();
      case 'documents':
        return renderDocumentsSection();
      case 'cta':
        return renderCTASection();
      default:
        return renderMainSection();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading about us data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          {/* Header */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">About Us Management</h1>
                <p className="text-gray-600 dark:text-gray-400">Manage your about us page content</p>
              </div>
              <div className="flex items-center gap-4">
                {isEditing ? (
                  <>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </button>
                    <button
                      onClick={saveAboutData}
                      disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Save Changes
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    <Edit className="w-4 h-4" />
                    Edit Content
                  </button>
                )}
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-6 py-4">
              <p className="text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <div className="flex">
            {/* Sidebar */}
            <div className="w-64 border-r border-gray-200 dark:border-gray-700">
              <nav className="p-4">
                <ul className="space-y-2">
                  {sections.map((section) => {
                    const IconComponent = section.icon;
                    return (
                      <li key={section.id}>
                        <button
                          onClick={() => setActiveSection(section.id)}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                            activeSection === section.id
                              ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          <IconComponent className="w-4 h-4" />
                          {section.label}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>

            {/* Main Content */}
            <div className="flex-1 p-4 sm:p-6">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>

      {/* Team Modal */}
      {showTeamModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingTeamMember ? 'Edit Team Member' : 'Add Team Member'}
              </h3>
              <button
                onClick={closeTeamModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={teamMemberForm.name}
                  onChange={(e) => handleTeamMemberChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Role *
                </label>
                <input
                  type="text"
                  value={teamMemberForm.role}
                  onChange={(e) => handleTeamMemberChange('role', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Senior Developer"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Bio
                </label>
                <textarea
                  value={teamMemberForm.bio}
                  onChange={(e) => handleTeamMemberChange('bio', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Brief description about the team member..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Image URL
                </label>
                <input
                  type="text"
                  value={teamMemberForm.image}
                  onChange={(e) => handleTeamMemberChange('image', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={teamMemberForm.email}
                  onChange={(e) => handleTeamMemberChange('email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  LinkedIn URL
                </label>
                <input
                  type="url"
                  value={teamMemberForm.linkedin_url}
                  onChange={(e) => handleTeamMemberChange('linkedin_url', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="https://linkedin.com/in/johndoe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Twitter URL
                </label>
                <input
                  type="url"
                  value={teamMemberForm.twitter_url}
                  onChange={(e) => handleTeamMemberChange('twitter_url', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="https://twitter.com/johndoe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  GitHub URL
                </label>
                <input
                  type="url"
                  value={teamMemberForm.github_url}
                  onChange={(e) => handleTeamMemberChange('github_url', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="https://github.com/johndoe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Website URL
                </label>
                <input
                  type="url"
                  value={teamMemberForm.website_url}
                  onChange={(e) => handleTeamMemberChange('website_url', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="https://johndoe.dev"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sort Order
                </label>
                <input
                  type="number"
                  value={teamMemberForm.sort_order}
                  onChange={(e) => handleTeamMemberChange('sort_order', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Active Status
                </label>
                <select
                  value={teamMemberForm.is_active ? '1' : '0'}
                  onChange={(e) => handleTeamMemberChange('is_active', e.target.value === '1')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Skills
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {teamMemberForm.skills.map((skill, index) => (
                    <span key={index} className="px-3 py-1 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 text-sm rounded-full flex items-center gap-2">
                      {skill}
                      <button
                        onClick={() => removeSkill(index)}
                        className="text-primary-500 hover:text-primary-700"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <button
                  onClick={addSkill}
                  className="px-3 py-1 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700"
                >
                  Add Skill
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={closeTeamModal}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={saveTeamMember}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                {editingTeamMember ? 'Update' : 'Create'} Team Member
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Modal */}
      {showDocumentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingDocument ? 'Edit Document' : 'Add Document'}
              </h3>
              <button
                onClick={closeDocumentModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={documentForm.title}
                  onChange={(e) => handleDocumentChange('title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Company Profile"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={documentForm.description}
                  onChange={(e) => handleDocumentChange('description', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Brief description of the document..."
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  File URL *
                </label>
                <input
                  type="url"
                  value={documentForm.file_url}
                  onChange={(e) => handleDocumentChange('file_url', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="https://example.com/document.pdf"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  File Type
                </label>
                <select
                  value={documentForm.file_type}
                  onChange={(e) => handleDocumentChange('file_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="pdf">PDF</option>
                  <option value="doc">DOC</option>
                  <option value="docx">DOCX</option>
                  <option value="xls">XLS</option>
                  <option value="xlsx">XLSX</option>
                  <option value="ppt">PPT</option>
                  <option value="pptx">PPTX</option>
                  <option value="jpg">JPG</option>
                  <option value="png">PNG</option>
                  <option value="gif">GIF</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  File Size
                </label>
                <input
                  type="text"
                  value={documentForm.file_size}
                  onChange={(e) => handleDocumentChange('file_size', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="2.5 MB"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Download Button Text
                </label>
                <input
                  type="text"
                  value={documentForm.download_button_text}
                  onChange={(e) => handleDocumentChange('download_button_text', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Download"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Download Button Icon
                </label>
                <select
                  value={documentForm.download_button_icon}
                  onChange={(e) => handleDocumentChange('download_button_icon', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="Download">Download</option>
                  <option value="FileText">File Text</option>
                  <option value="Briefcase">Briefcase</option>
                  <option value="Award">Award</option>
                  <option value="Star">Star</option>
                  <option value="Heart">Heart</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sort Order
                </label>
                <input
                  type="number"
                  value={documentForm.sort_order}
                  onChange={(e) => handleDocumentChange('sort_order', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Active Status
                </label>
                <select
                  value={documentForm.is_active ? '1' : '0'}
                  onChange={(e) => handleDocumentChange('is_active', e.target.value === '1')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={closeDocumentModal}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={saveDocument}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                {editingDocument ? 'Update' : 'Create'} Document
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutUsManagement; 